import { UseQueryOptions } from '@tanstack/react-query';

import { ResourceType } from '../../resources';
import { getResourceQueryKey, useResourceQuery } from '../../resources/queries/use-resource-query.hook';
import type { LocationQueryData, LocationQueryField } from '../types';

export const DEFAULT_LOCATION_FIELDS = [
	'_id',
	'name',
	'icon',
	'parentLocation',
	'rootLocation',
	'businessHours',
	'address',
	'geo',
	'timezone',
	'bookingSettings',
	'closedDays',
] as const;

export type LocationQueryVariables<F extends readonly LocationQueryField[] = typeof DEFAULT_LOCATION_FIELDS> = {
	id: string;
	fields?: F;
};

export const useLocationQuery = <
	const F extends readonly LocationQueryField[] = typeof DEFAULT_LOCATION_FIELDS,
	TData = LocationQueryData<F[number]>,
	TError = unknown,
>(
	variables: LocationQueryVariables<F>,
	options?: UseQueryOptions<LocationQueryData<F[number]>, TError, TData>
) => {
	const fields = variables.fields ?? DEFAULT_LOCATION_FIELDS;

	return useResourceQuery<LocationQueryData<F[number]>, LocationQueryData<F[number]>, TError, TData>(
		{
			resourceId: variables.id,
			_fields: fields as unknown as string[],
		},
		{
			resourceType: ResourceType.LOCATIONS,
			defaultFields: [...DEFAULT_LOCATION_FIELDS],
			queryKeyPrefix: ResourceType.LOCATIONS,
		},
		options
	);
};

export const getLocationQueryKey = (locationId: string, fields?: readonly LocationQueryField[]) => {
	return getResourceQueryKey(
		{ resourceId: locationId, ...(fields ? { _fields: fields as unknown as string[] } : {}) },
		ResourceType.LOCATIONS,
		ResourceType.LOCATIONS
	);
};
